import { CircularProgress } from '@jouzen/ecom-components/v3/CircularProgress';
import { AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';

import Motion from '@/app/components/Motion';

import DepartmentJobsList from './DepartmentJobsList';
import DepartmentsList from './DepartmentsList';
import DepartmentsListAlert from './DepartmentsListAlert';
import type { DepartmentsByName, GreenhouseFlattenedJob } from './types';

export interface JobBoardContentsProps {
  readonly onSelectDepartment?: (departmentName: string | null) => void;
  readonly isLoading: boolean;
  readonly hasError: boolean;
  readonly jobs: GreenhouseFlattenedJob[] | undefined;
}

const JobBoardContents = ({
  onSelectDepartment,
  isLoading,
  hasError,
  jobs,
}: JobBoardContentsProps) => {
  const [selectedOfficeId, setSelectedOfficeId] = useState<number | null>(null);
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<
    string | null
  >(null);

  const jobsFilteredByRegion = useMemo(() => {
    if (!jobs) return [];
    if (!selectedOfficeId) return jobs;
    return jobs.filter((job) =>
      job.officePath.some((officeId) => officeId === selectedOfficeId),
    );
  }, [jobs, selectedOfficeId]);

  const departmentsByName = useMemo(
    () =>
      jobsFilteredByRegion.reduce((acc, job) => {
        if (!acc[job.department.name]) {
          acc[job.department.name] = {
            ...job.department,
            jobs: [job],
          };
        } else {
          acc[job.department.name]!.jobs.push(job);
        }
        return acc;
      }, {} as DepartmentsByName),
    [jobsFilteredByRegion],
  );

  const handleSetSelectedDepartmentName = (departmentName: string | null) => {
    setSelectedDepartmentName(departmentName);
    onSelectDepartment?.(departmentName);
  };

  const handleClearSelectedDepartment = () => setSelectedDepartmentName(null);

  const handleSetSelectedOfficeId = (officeId: number | null) => {
    setSelectedOfficeId(officeId);
  };

  if (isLoading) {
    return (
      <div className="divide-y divide-sandstone-500 border-t-2 border-b border-sandstone-500">
        <div
          className="flex justify-center py-24"
          data-cy="careers-list-loading"
        >
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="divide-y divide-sandstone-500 border-t-2 border-b border-sandstone-500">
        <DepartmentsListAlert message="checkout_validate_params_exception" />
      </div>
    );
  }

  return (
    <AnimatePresence initial={false} mode="wait">
      {selectedDepartmentName && departmentsByName[selectedDepartmentName] ? (
        <Motion
          key="jobsForSelectedDepartment"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <DepartmentJobsList
            department={departmentsByName[selectedDepartmentName]}
            clearSelectedDepartment={handleClearSelectedDepartment}
          />
        </Motion>
      ) : (
        <Motion
          key="departmentsList"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <DepartmentsList
            onRegionFilterChange={handleSetSelectedOfficeId}
            selectedRegionFilter={selectedOfficeId}
            hasJobs={Boolean(jobsFilteredByRegion.length)}
            departmentsByName={departmentsByName}
            onClickDepartment={handleSetSelectedDepartmentName}
          />
        </Motion>
      )}
    </AnimatePresence>
  );
};

export default JobBoardContents;
